import { useMemo, useState } from 'react';

import { sortBy } from 'lodash';
import { useQueryClient } from 'react-query';

import { ReportTemplate } from 'api/endPoints/GetReportTemplateList';

import { FAVORITES } from 'components/Dashboards/Reporting/reporting.consts';
import { UseReportTemplates } from 'components/widgets/ReportsHome/ReportsTemplates/ReportsTemplates.provider';

export type UseReportsTemplatesLogic = ReturnType<typeof useReportsTemplatesLogic>;

interface Props {
    api: UseReportTemplates['api'];
}

export const useReportsTemplatesLogic = ({ api }: Props) => {
    const [searchByName, setSearchByName] = useState('');
    const [selectedTag, setSelectedTag] = useState<ISelectOption<number> | null>(null);

    const queryClient = useQueryClient();

    const deleteTemplateCard = async (reportTemplateId: number) => {
        await api.deleteReportTemplate.instance.mutateAsync({ templateId: reportTemplateId });
        await queryClient.invalidateQueries('getReportTemplateList');
    };

    const getSearchValue = () => searchByName;
    const handleSearchChange = (newValue: string) => {
        setSearchByName(newValue);
    };

    const getSelectedTag = () => selectedTag;

    const tagOptions = useMemo(() => {
        const options: { value: number; label: string }[] = [];
        if (!api.getTagList.data) return options;
        api.getTagList.data.forEach((tag) => {
            if (tag.name !== FAVORITES) {
                options.push({
                    value: tag.id,
                    label: tag.name,
                });
            }
        });
        return options;
    }, [api.getTagList.data]);

    const handleTagSelectionChange = (selectedTag: ISelectOption<number> | null) => {
        setSelectedTag(selectedTag);
    };

    const handleClearFilters = () => {
        setSearchByName('');
        setSelectedTag(null);
    };

    const templateListSortedByName = useMemo(() => {
        if (api.getReportTemplates.data === undefined) return [];
        return sortBy(api.getReportTemplates.data, [(template) => template.name.toLowerCase()]);
    }, [api.getReportTemplates.data]);

    const filterByName = (templates: ReportTemplate[], searchTerm: string) => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return templates.filter((template) => template.name.toLowerCase().includes(lowerCaseSearchTerm));
    };

    const filterByTag = (templates: ReportTemplate[], selectedTagValue: number) => {
        return templates.filter((template) => template.tags.some((tag) => tag.id === selectedTagValue));
    };

    const templateList = useMemo(() => {
        if (templateListSortedByName.length === 0) return templateListSortedByName;
        let templatesList = [...templateListSortedByName];

        if (searchByName !== '') {
            templatesList = filterByName(templatesList, searchByName);
        }

        if (selectedTag) {
            templatesList = filterByTag(templatesList, selectedTag.value);
        }

        return templatesList;
    }, [templateListSortedByName, searchByName, selectedTag]);

    return {
        logic: {
            getSearchValue,
            templateList,
            handleSearchChange,
            deleteTemplateCard,
            handleClearFilters,
            getSelectedTag,
            handleTagSelectionChange,
            tagOptions,
        },
    };
};
